/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {
  SelectProps as MuiSelectProps,
  Select as MuiSelect,
  FormHelperText as MuiFormHelperText,
  MenuItem,
} from '@mui/material';
import clsx from 'clsx';
import { InputLabel } from '../inputLabel';
import { SelectLoadingIndicator } from './SelectLoadingIndicator';
import { projectPrefix } from '../constants';
import { selectClasses } from './types';
import { menuClasses } from '../menu';
import useFormFieldId from '../utilities/useFormFieldId';
import { FormControl, FormControlOwnProps, FormControlProps } from '../form';

export interface SelectProps extends Omit<MuiSelectProps, 'variant' | 'label' | 'ref'>, FormControlOwnProps {
  /**
   * Select size
   * @default medium
   */
  size?: FormControlProps['size'];
  /**
   * Children node for SelectLabel component
   */
  label?: React.ReactNode;
  /**
   * Children node for FormHelperText component
   */
  helperText?: React.ReactNode;
  /**
   * If true children of Select will not be displayed and instead SelectLoadingIndicator will
   * be present.
   * @default false
   */
  loading?: boolean;
  /**
   * Value used when placeholder should be displayed
   * @default ""
   */
  emptyValue?: any;

  ref?: React.RefObject<HTMLDivElement>;
}

export const Select = React.forwardRef(
  (props: SelectProps, ref: React.RefObject<HTMLDivElement>) => {
    const {
      id: idOverride, label, labelId, size, className, fullWidth,
      error, helperText, placeholder, displayEmpty, emptyValue = '',
      loading, children, required, multiple, MenuProps, labelPosition, ...rest
    } = props;

    const [id, helperTextId, inputLabelId] = useFormFieldId({
      idOverride, helperText, label,
    });

    return (
      <FormControl
        ref={ref}
        size={size}
        className={className}
        fullWidth={fullWidth}
        error={error}
        required={required}
        labelPosition={labelPosition}
      >
        {label != null && label !== '' && (
          <InputLabel
            className={clsx(
              selectClasses.label,
              { [`${className}-label`]: className },
            )}
            id={labelId || inputLabelId}
            required={required}
            size={size}
          >
            {label}
          </InputLabel>
        )}

        <MuiSelect
          id={id}
          aria-describedby={helperTextId}
          labelId={labelId || inputLabelId}
          className={clsx({ [`${className}-select`]: className })}
          displayEmpty={displayEmpty !== undefined ? displayEmpty : Boolean(placeholder)}
          multiple={multiple}
          MenuProps={{
            ...MenuProps,
            className: clsx(
              MenuProps?.className,
              {
                [menuClasses.multiple]: multiple,
              },
            ),
          }}
          {...rest}
        >
          {loading
            ? (
              <SelectLoadingIndicator
                className={clsx({ [`${className}-loadingIndicator`]: className })}
              />
            )
            : [
              placeholder
                ? (
                  <MenuItem
                    key={`${projectPrefix}PlaceholderItem`}
                    value={emptyValue}
                    disabled
                    className={selectClasses.placeholder}
                  >
                    <span className={selectClasses.placeholder}>
                      {placeholder}
                    </span>
                  </MenuItem>
                )
                : undefined,
              children,
            ]}
        </MuiSelect>

        {helperText && (
          <MuiFormHelperText
            id={helperTextId}
            className={clsx({ [`${className}-helperText`]: className })}
          >
            {helperText}
          </MuiFormHelperText>
        )}
      </FormControl>
    );
  },
);
